import React from 'react';
import { Box, InputBase } from '@mui/material';
import { LuSend } from 'react-icons/lu';
import { elliottTokens as t } from './tokens';

type ChatInputBarProps = {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  canSend: boolean; // whether the current value is valid to submit
  readOnly?: boolean; // current step takes a selection — block typing but keep the look
  placeholder?: string;
};

// Brandy-glow reply bar — the signature Elliott input. Its appearance never
// changes between steps: on selection steps we simply make it read-only so the
// admin can't type, but the box, border, glow and placeholder stay constant.
const ChatInputBar: React.FC<ChatInputBarProps> = ({
  value,
  onChange,
  onSend,
  canSend,
  readOnly = false,
  placeholder = 'Reply to Elliott'
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      px: '12px',
      py: '12px',
      borderRadius: `${t.radius}px`,
      bgcolor: t.surface,
      // The brandy glow lives on the reply bar only when it's the active action.
      // When locked (a selection is needed), it's muted and the options carry
      // the highlight instead.
      border: `1.5px solid ${readOnly ? t.borderDim : t.brandLight}`,
      boxShadow: readOnly
        ? 'none'
        : '0px 0px 16px 0px rgba(244,119,35,0.5), inset 0px 0px 8px 0px rgba(247,148,32,0.25)',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
    }}
  >
    <InputBase
      multiline
      maxRows={4}
      value={value}
      readOnly={readOnly}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          if (canSend) onSend();
        }
      }}
      placeholder={placeholder}
      sx={{
        flex: 1,
        color: t.textPrimary,
        fontSize: t.bodySize,
        fontWeight: 500,
        lineHeight: 'normal',
        '& textarea, & input': {
          backgroundColor: 'transparent',
          whiteSpace: 'pre-wrap',
          cursor: readOnly ? 'default' : 'text'
        },
        '& textarea::placeholder, & input::placeholder': { color: t.textTertiary, opacity: 1 }
      }}
    />
    <Box
      onClick={canSend ? onSend : undefined}
      sx={{
        display: 'flex',
        flexShrink: 0,
        color: canSend ? t.brandLight : t.textTertiary,
        cursor: canSend ? 'pointer' : 'default'
      }}
      aria-label="Send"
    >
      <LuSend size={20} />
    </Box>
  </Box>
);

export default ChatInputBar;
